import type { PropsWithChildren } from "react";
import { useState } from "react";
import { styled, ThemeProvider } from "styled-components";

import { InfinityLoader, StyledGrid } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { maxWidth } from "common/styles/mediaQueries/breakpoints";
import { Status } from "common/types/status";
import { useTranslate } from "common/utilities/useTranslate";
import { useSession } from "context/session/useSession";
import { darkTheme } from "domains/theme/constants/theme";
import { TopicTile } from "domains/topic/components/topicTile/TopicTile";
import type { Topic } from "entities/topics/types";

type CollectionSearchableTopicListProps = PropsWithChildren & {
  collection: { id: string; name: string };
  loadNextPage: () => void;
  nextPageMayExist: boolean;
  status: Status;
  topics: Topic[];
};

export const CollectionSearchableTopicList = ({
  children,
  collection,
  loadNextPage,
  nextPageMayExist,
  status,
  topics,
}: CollectionSearchableTopicListProps) => {
  const translate = useTranslate();
  const { updateSession } = useSession();
  const [searchValue, setSearchValue] = useState<string>("");

  const filteredTopics = topics.filter((topic) => topic.name.toLowerCase().includes(searchValue.toLowerCase().trim()));

  const setCollectionPageScrollPosition = () =>
    updateSession((state) => ({ ...state, scrollPositions: { ...state.scrollPositions, collectionPage: scrollY } }));

  return (
    <Container>
      <ThemeProvider theme={darkTheme}>
        <SearchBar>
          {children}
          <UIInput
            clear={() => setSearchValue("")}
            onChange={setSearchValue}
            placeholder={translate("app.collection.search")}
            value={searchValue}
          />
        </SearchBar>
      </ThemeProvider>
      {status === Status.Error || (
        <Loader hasNextPage={nextPageMayExist} isPending={status === Status.Loading} loadNextPage={loadNextPage}>
          <StyledGrid>
            {filteredTopics.map((topic) => (
              <TopicTile
                key={topic.id}
                topicId={topic.id}
                topicName={topic.name}
                provider={topic.provider}
                topicBack={{ collection }}
                attachment={topic.attachment ?? undefined}
                canEdit={topic.can_edit ?? false}
                isCustom={topic.is_custom ?? false}
                isNew={topic.is_new ?? false}
                isFollowing={topic.is_following ?? false}
                imageUrl={topic.images?.x_small}
                hideOwnershipLabel
                onClick={setCollectionPageScrollPosition}
                pageForTracking="Collection"
              />
            ))}
          </StyledGrid>
        </Loader>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding-bottom: 2rem;
`;

const SearchBar = styled.div`
  background-color: ${({ theme }) => theme.colors.black};
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 0 3rem 1.5rem;

  & > div {
    width: fit-content;
  }

  @media ${maxWidth.medium} {
    flex-direction: column;
    padding: 1rem 1rem 1.5rem;
  }
`;

const Loader = styled(InfinityLoader)`
  box-sizing: border-box;
  padding: 0 3rem;

  @media ${maxWidth.medium} {
    padding: 0 1rem;
  }
`;
